import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, Clock, Users, TrendingUp, Scale } from 'lucide-react';
import { API } from '../App';
import Header from '../components/layout/Header';

const VERDICT_COLORS = { Pass: '#22C55E', Conditional: '#F59E0B', Fail: '#EF4444' };

export default function Gallery() {
  const [frays, setFrays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadGallery();
  }, [page]);

  const loadGallery = async () => {
    setLoading(true);
    try {
      const res = await API.get(`/api/gallery?page=${page}&limit=12`);
      setFrays(res.data.frays || []);
      setTotal(res.data.total || 0);
    } catch {}
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-6 max-w-6xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold font-['Barlow_Condensed']" data-testid="gallery-title">Fray Gallery</h1>
            <span className="text-[10px] px-2 py-0.5 border border-[#FF4D00]/30 text-[#FF4D00] font-mono">PUBLIC</span>
          </div>
          <p className="text-[#A0A0A0]">Browse completed Frays shared by the community. See how AI committees debate real decisions.</p>
          {total > 0 && <p className="text-xs font-mono text-[#A0A0A0] mt-2">{total} public Frays</p>}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => <div key={i} className="h-56 animate-shimmer" />)}
          </div>
        ) : frays.length === 0 ? (
          <div className="text-center py-20 border border-[#2A2A2A]" data-testid="gallery-empty">
            <Scale className="w-12 h-12 text-[#2A2A2A] mx-auto mb-4" />
            <h2 className="text-xl font-bold font-['Barlow_Condensed'] mb-2">No Public Frays Yet</h2>
            <p className="text-[#A0A0A0] mb-6">Be the first to share a Fray with the community.</p>
            <Link to="/playbooks" className="px-6 py-2 bg-[#FF4D00] text-black text-sm font-medium inline-block">
              Start a Fray
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-grid">
            {frays.map((fray, i) => {
              const verdict = fray.verdict || {};
              return (
                <motion.div
                  key={fray.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                >
                  <Link
                    to={`/share/${fray.share_token}`}
                    className="block p-5 bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#FF4D00]/40 transition-all group h-full"
                    data-testid={`gallery-card-${i}`}
                  >
                    {/* Verdict */}
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-xs font-mono text-[#A0A0A0]">{fray.template_name || 'Custom'}</span>
                      {verdict.verdict && (
                        <span className="text-xs font-mono px-2 py-0.5 border font-semibold"
                          style={{ color: VERDICT_COLORS[verdict.verdict] || '#FF4D00', borderColor: (VERDICT_COLORS[verdict.verdict] || '#FF4D00') + '40' }}>
                          {verdict.verdict}
                        </span>
                      )}
                    </div>

                    {/* Scenario preview */}
                    <p className="text-sm text-[#F5F5F5] mb-3 line-clamp-2 group-hover:text-[#FF4D00] transition-colors leading-relaxed">
                      {fray.scenario?.slice(0, 120)}{fray.scenario?.length > 120 ? '...' : ''}
                    </p>

                    {/* Hot take */}
                    {verdict.hot_take_quote && (
                      <div className="p-2 bg-[#FF4D00]/5 border border-[#FF4D00]/10 mb-3">
                        <p className="text-[10px] text-[#FF4D00] italic line-clamp-2">"{verdict.hot_take_quote}"</p>
                      </div>
                    )}

                    {/* Voices */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {fray.voices?.slice(0, 4).map((v, vi) => (
                        <span key={vi} className="text-[9px] px-1.5 py-0.5 border border-[#2A2A2A] font-mono" style={{ color: v.color || '#A0A0A0' }}>
                          {v.name}
                        </span>
                      ))}
                      {(fray.voices?.length || 0) > 4 && (
                        <span className="text-[9px] px-1.5 py-0.5 border border-[#2A2A2A] text-[#A0A0A0] font-mono">
                          +{fray.voices.length - 4}
                        </span>
                      )}
                    </div>

                    {/* Meta */}
                    <div className="flex items-center gap-3 text-[9px] text-[#A0A0A0] font-mono pt-3 border-t border-[#2A2A2A]">
                      {fray.tension_score > 0 && (
                        <span className="flex items-center gap-1">
                          <Zap className="w-2.5 h-2.5 text-[#FF4D00]" /> {fray.tension_score}%
                        </span>
                      )}
                      {fray.duration_seconds > 0 && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-2.5 h-2.5" /> {Math.round(fray.duration_seconds / 60)}m
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Users className="w-2.5 h-2.5" /> {fray.voices?.length || 0}
                      </span>
                      <span className="ml-auto">{fray.author_name}</span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {total > 12 && (
          <div className="flex justify-center gap-2 mt-8">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-4 py-1.5 border border-[#2A2A2A] text-xs font-mono text-[#A0A0A0] disabled:opacity-30">Prev</button>
            <span className="px-4 py-1.5 text-xs font-mono text-[#A0A0A0]">Page {page}</span>
            <button onClick={() => setPage(p => p + 1)} disabled={frays.length < 12}
              className="px-4 py-1.5 border border-[#2A2A2A] text-xs font-mono text-[#A0A0A0] disabled:opacity-30">Next</button>
          </div>
        )}
      </div>
    </div>
  );
}
